import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, CheckCircle, FileText, Building2, Scale, Receipt,
  Calendar, Clock, Facebook, Linkedin, Twitter,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicContactStrip from "@/components/PublicContactStrip";
import SEOTrustBlock from "@/components/SEOTrustBlock";
import NewsletterSignup from "@/components/NewsletterSignup";

/* eslint-disable react/no-unescaped-entities */

// One canonical source of truth for every province / territory landing
// page. Each entry generates a /landlord-software/:slug route with deep
// province-specific copy + JSON-LD + FAQ + filtered blog feed.
const PROVINCES = [
  {
    slug: "ontario", code: "ON", name: "Ontario",
    pop: "15.5M", tagline: "Built around the Ontario Standard Lease, the LTB, and N1–N13 notices.",
    pains: [
      "Rent increases capped at the provincial guideline (1.5% in 2024, 2.5% in 2025) — miss the math and your N1 gets tossed.",
      "Mandatory Ontario Standard Lease (Form 2229E) since 2018 — using your own template makes the lease unenforceable after 30 days notice.",
      "LTB hearing backlogs of 8–12 months — a single clerical error on an N4 sends you back to the start of the queue.",
    ],
    features: [
      { icon: FileText, t: "Ontario Standard Lease generator", d: "Form 2229E with all required clauses, additional terms validated against the LTB's published list of unenforceable terms." },
      { icon: Scale, t: "N1–N13 notice library", d: "Every notice number, autofilled with the right tenant addresses, served date, and termination date math." },
      { icon: Receipt, t: "Annual rent increase guideline", d: "The current and previous years' guideline percentages built in — and 90-day notice tracking so N1s never go out late." },
    ],
    cities: ["Toronto", "Ottawa", "Hamilton", "Mississauga", "London", "Kitchener-Waterloo", "Windsor"],
    blogCategories: ["Ontario", "Taxes"],
    faqs: [
      { q: "What's the maximum rent increase in Ontario in 2026?", a: "The Government of Ontario publishes the annual rent increase guideline each summer for the following calendar year. Central Rentals shows the current guideline inside the rent increase tool so your N1 always matches." },
      { q: "Is the Ontario Standard Lease mandatory?", a: "Yes. Since April 30, 2018, all private residential tenancies in Ontario must use the Ontario Standard Lease (Form 2229E). If you don't, the tenant can give you 60 days' written notice asking for one, and after 30 more days they can withhold one month's rent." },
      { q: "How do I evict a tenant in Ontario?", a: "You must serve the correct N-form (most commonly N4 for unpaid rent or N12 for owner-use), wait the legally-required notice period, then file an L-form with the Landlord and Tenant Board. Self-help eviction is illegal and exposes you to up to $50,000 in fines." },
    ],
  },
  {
    slug: "british-columbia", code: "BC", name: "British Columbia",
    pop: "5.6M", tagline: "Built around the RTB, the 2-month rent cap, and BC's strict notice-form rules.",
    pains: [
      "The annual rent cap (3.5% for 2025) is one of the most aggressively-enforced in Canada — a 1¢ over and the entire notice is void.",
      "RTB Form RTB-12 / RTB-32 series each have different service rules — most landlords serve at least one wrong each year.",
      "Vancouver and the Capital Regional District layer on extra short-term rental bylaws on top of the provincial Strata + RTA rules.",
    ],
    features: [
      { icon: FileText, t: "RTB-1 + RTB-12 notice generator", d: "Every BC notice — including the new versions effective Dec 2024 — with the right service-window math and proof-of-service log." },
      { icon: Scale, t: "BC rent increase calculator", d: "Annual cap plus the 12-month and 3-month notice rules. Auto-blocks an increase if the previous increase was less than 12 months ago." },
      { icon: Building2, t: "Strata + STR bylaw check", d: "Cross-references provincial STR rules with Vancouver, Victoria, Whistler, and Tofino bylaw overlays before you list on Airbnb." },
    ],
    cities: ["Vancouver", "Victoria", "Surrey", "Burnaby", "Richmond", "Kelowna", "Nanaimo"],
    blogCategories: ["British Columbia"],
    faqs: [
      { q: "What's the BC rent increase cap for 2026?", a: "The BC government sets the cap each September for the following calendar year. The 2025 cap is 3.0%. Central Rentals pulls the live figure from the RTB feed so your notice is always within the legal limit." },
      { q: "Do I need to use RTB forms?", a: "Yes. BC's Residential Tenancy Act requires landlords to use the RTB's official forms for notices to end tenancy, rent increases, and dispute resolution applications. Custom forms are void." },
      { q: "How long does an RTB hearing take?", a: "Direct Request (unpaid rent) decisions are usually issued in 2–4 weeks. Participatory hearings (most other disputes) currently take 3–5 months." },
    ],
  },
  {
    slug: "alberta", code: "AB", name: "Alberta",
    pop: "4.7M", tagline: "Built for Alberta's RTDRS, no rent cap, and the 14-day security deposit interest rule.",
    pains: [
      "Alberta is the only province with no rent control — that flexibility creates tenant turnover, which means more frequent screening, leases, and notices.",
      "Mandatory security deposit interest at the provincial rate, paid annually — and the rate changes every calendar year.",
      "RTDRS vs Provincial Court of Alberta vs Court of King's Bench — picking the wrong venue costs you weeks.",
    ],
    features: [
      { icon: Receipt, t: "Security deposit interest calc", d: "Annual interest at the published Alberta rate, compounded correctly per the Security Deposit Interest Regulation." },
      { icon: FileText, t: "14-day notice generator", d: "Substantial breach notices, non-payment notices, and termination notices with the Alberta-specific service-method rules built in." },
      { icon: Scale, t: "RTDRS application packager", d: "Auto-prepares the application package with your tenancy agreement, ledger, photos, and proof of service in one PDF." },
    ],
    cities: ["Calgary", "Edmonton", "Red Deer", "Lethbridge", "Medicine Hat", "Fort McMurray", "Grande Prairie"],
    blogCategories: ["Alberta"],
    faqs: [
      { q: "Does Alberta have rent control?", a: "No. Alberta is the only province in Canada with no statutory rent increase cap. Landlords can raise rent by any amount on a periodic tenancy with proper notice (3 months for monthly tenancies). Central Rentals still tracks 1-year minimum spacing automatically." },
      { q: "What's the current security deposit interest rate in Alberta?", a: "The rate is set annually by the Government of Alberta. Central Rentals pulls the published rate each January and applies it to every deposit on file — landlords get a one-click annual interest report." },
      { q: "RTDRS or court — which one should I use?", a: "RTDRS for most landlord-tenant disputes under $50,000 (faster, cheaper). Court of King's Bench for evictions involving complex legal questions or claims over $50,000." },
    ],
  },
  {
    slug: "quebec", code: "QC", name: "Quebec",
    pop: "8.8M", tagline: "Built around the TAL, the F-form lease, and Quebec's unique rent fixation rules.",
    pains: [
      "TAL (Tribunal administratif du logement) wait times of 12–24 months for non-payment cases.",
      "The mandatory Form F lease in French — sublease and assignment rules differ from every other province.",
      "Rent fixation: tenants can challenge any increase by applying to the TAL, often resulting in a $5–10 monthly increase against your $50 ask.",
    ],
    features: [
      { icon: FileText, t: "Form F (bail) generator", d: "Full French + English bilingual lease with mandatory Section G ('rent paid in the previous 12 months') auto-filled." },
      { icon: Scale, t: "TAL filing packager", d: "Application + supporting documents formatted for direct upload to TAL's e-services portal." },
      { icon: Receipt, t: "Rent fixation calculator", d: "TAL's official methodology — operating expenses, taxes, capital improvements — to estimate the increase that will survive a challenge." },
    ],
    cities: ["Montréal", "Québec City", "Laval", "Gatineau", "Longueuil", "Sherbrooke", "Saguenay"],
    blogCategories: ["Quebec"],
    faqs: [
      { q: "Comment se calcule la hausse de loyer au Québec?", a: "Le TAL publie chaque janvier une grille de calcul officielle. Central Rentals prend cette grille et calcule automatiquement l'augmentation que vous pouvez justifier en cas de contestation." },
      { q: "Is the Quebec Form F lease mandatory?", a: "Yes — the Form F (bail) issued by the TAL is the only valid residential lease in Quebec. Side agreements are unenforceable unless they're attached as an addendum to the Form F." },
      { q: "How long does TAL take to process a non-payment case?", a: "TAL's published target is 1.5 months for non-payment. Real-world averages in 2024–2025 are 12–24 months. Central Rentals tracks every case file so you can demonstrate the cumulative arrears at hearing." },
    ],
  },
  {
    slug: "manitoba", code: "MB", name: "Manitoba",
    pop: "1.5M",
    tagline: "Built around the Residential Tenancies Branch, annual guideline increases, and prairie-grade tenant turnover.",
    pains: ["Annual rent increase guideline published every fall — most landlords miss the 3-month notice window.", "RTB requires written tenancy agreements above a 1-year term — many small landlords still use verbal.", "Winnipeg-specific Vital Statistics Act notice requirements layered on top of provincial RTA."],
    features: [
      { icon: FileText, t: "RTB-compliant lease", d: "Provincial rental agreement with the required terms under the Residential Tenancies Act." },
      { icon: Receipt, t: "Annual guideline tracker", d: "Manitoba's annual rent increase guideline auto-pulled and notice math built in." },
      { icon: Scale, t: "RTB filing helper", d: "Application package generator for unpaid rent, repair orders, and termination disputes." },
    ],
    cities: ["Winnipeg", "Brandon", "Steinbach", "Thompson", "Portage la Prairie"],
    blogCategories: ["Manitoba"],
    faqs: [
      { q: "What's the Manitoba rent increase guideline?", a: "The Residential Tenancies Branch publishes the annual guideline each fall for the following calendar year. Central Rentals pulls it live so your notice math is always current." },
      { q: "Do I need a written lease in Manitoba?", a: "For any tenancy over 12 months, yes. Below that, verbal is enforceable but high-risk — every Manitoba landlord we work with switches to written within 6 months." },
    ],
  },
  {
    slug: "saskatchewan", code: "SK", name: "Saskatchewan",
    pop: "1.2M",
    tagline: "Built for the Office of Residential Tenancies, no rent cap, and Saskatchewan's strict 15-day non-payment notice.",
    pains: ["15-day non-payment notice has tight service rules — wrong service method = re-start at day 0.", "Saskatchewan's claim damages process at ORT is fast but requires perfect documentation.", "No annual rent cap but tenants have a strong right of first refusal that landlords often forget about."],
    features: [
      { icon: FileText, t: "15-day notice generator", d: "ORT-compliant non-payment, breach, and termination notices with auto-served-date math." },
      { icon: Scale, t: "ORT application packager", d: "Hearing applications formatted for ORT's e-filing system, with bundled exhibits." },
      { icon: Receipt, t: "Damage deposit ledger", d: "Inspection report + photo evidence + deposit deduction worksheet ready for ORT submission." },
    ],
    cities: ["Saskatoon", "Regina", "Prince Albert", "Moose Jaw", "Lloydminster"],
    blogCategories: ["Saskatchewan"],
    faqs: [
      { q: "Does Saskatchewan have rent control?", a: "No. Like Alberta, Saskatchewan has no statutory rent cap. Landlords can raise rent with proper notice once per 12-month period." },
      { q: "What's the ORT?", a: "The Office of Residential Tenancies — Saskatchewan's tribunal for landlord-tenant disputes. Most decisions are issued within 30 days of hearing." },
    ],
  },
  {
    slug: "nova-scotia", code: "NS", name: "Nova Scotia",
    pop: "1.0M",
    tagline: "Built around the Residential Tenancies Program, the 5% rent cap (extended), and Halifax STR bylaws.",
    pains: ["Provincial rent cap extended at 5% through 2027 — and Halifax has additional rules.", "Residential Tenancies Program backlogs can push non-payment files 4–6 months.", "Halifax Regional Municipality's STR registry requires annual renewal — easy to miss."],
    features: [
      { icon: Receipt, t: "5% cap calculator", d: "Auto-blocks any notice above the cap and shows the maximum lawful increase per unit." },
      { icon: FileText, t: "Form D notice generator", d: "Termination notices for non-payment, end-of-term, and breach with NS-specific service rules." },
      { icon: Building2, t: "HRM STR registry tracker", d: "Renewal reminders + provincial registry check for short-term rental licences." },
    ],
    cities: ["Halifax", "Dartmouth", "Sydney", "Truro", "New Glasgow"],
    blogCategories: ["Nova Scotia"],
    faqs: [
      { q: "What's the Nova Scotia rent cap?", a: "5% per year, extended through December 31, 2027. Applies to fixed-term and periodic tenancies that started before the cap. Central Rentals tracks the cap expiry per unit." },
      { q: "How long does a hearing take at the Residential Tenancies Program?", a: "Direct-decision (unpaid rent) targets are 30–45 days. Contested hearings: 60–120 days." },
    ],
  },
  {
    slug: "new-brunswick", code: "NB", name: "New Brunswick",
    pop: "820K",
    tagline: "Built around the Residential Tenancies Tribunal, the 7.5% (2025) rent cap pilot, and bilingual leases.",
    pains: ["Rent cap pilot creates uncertainty year over year — the 2025 cap differs from 2024.", "RTT requires bilingual (EN/FR) notices in many cases.", "Tenant notice periods are longer than most provinces — easy to miscount."],
    features: [
      { icon: FileText, t: "Bilingual lease generator", d: "EN + FR side-by-side rental agreement compliant with the Residential Tenancies Act." },
      { icon: Receipt, t: "Annual cap tracker", d: "Tracks the current rent cap pilot rate + auto-blocks over-cap notices." },
      { icon: Scale, t: "RTT filing helper", d: "Application + evidence package formatted for the Residential Tenancies Tribunal." },
    ],
    cities: ["Moncton", "Saint John", "Fredericton", "Dieppe", "Riverview"],
    blogCategories: ["New Brunswick"],
    faqs: [
      { q: "Does NB require bilingual leases?", a: "Not strictly, but bilingual leases dramatically reduce dispute risk in a province where 32% of residents are francophone. Central Rentals generates EN+FR by default." },
    ],
  },
  {
    slug: "newfoundland-and-labrador", code: "NL", name: "Newfoundland and Labrador",
    pop: "540K",
    tagline: "Built for the Residential Tenancies Division, NL's seasonal rental rules, and remote-property workflows.",
    pains: ["Long winters mean more maintenance — and the RTA has specific 'fit for habitation' rules around heat and water.", "RT Division hearings can require in-person attendance in St. John's — remote landlords often hire local counsel for $1000+/case.", "NL has no rent cap but strong notice-period rules — 3 months for monthly tenancies."],
    features: [
      { icon: FileText, t: "RTA-compliant lease", d: "NL standard residential rental agreement with required heat/water/repair clauses." },
      { icon: Scale, t: "RT Division filing helper", d: "Application + remote-attendance request package for landlords outside St. John's." },
      { icon: Building2, t: "Maintenance scheduler", d: "Winter readiness + spring inspection templates aligned to NL's habitability requirements." },
    ],
    cities: ["St. John's", "Mount Pearl", "Corner Brook", "Conception Bay South", "Grand Falls-Windsor"],
    blogCategories: ["Newfoundland and Labrador"],
    faqs: [
      { q: "Does NL have rent control?", a: "No statutory rent cap, but strong notice rules: 3 months' written notice for any rent increase on a monthly tenancy." },
    ],
  },
  {
    slug: "prince-edward-island", code: "PE", name: "Prince Edward Island",
    pop: "175K",
    tagline: "Built around IRAC, PEI's allowed annual increase, and the islands's short-term rental registry.",
    pains: ["IRAC sets the annual allowed increase each year — exceeding it requires a separate application.", "PEI's short-term rental tax (4.5%) is collected by hosts — auto-calc avoids audit pain.", "IRAC hearings are smaller-volume but require very specific evidence."],
    features: [
      { icon: Receipt, t: "Allowed increase tracker", d: "PEI's annual allowed rent increase auto-pulled from IRAC + over-cap blocker." },
      { icon: FileText, t: "IRAC application templates", d: "Forms 1–8 with PEI-specific service-method rules built in." },
      { icon: Building2, t: "STR tax + registry helper", d: "Calculates the 4.5% provincial sales tax on STR stays and tracks registry renewal." },
    ],
    cities: ["Charlottetown", "Summerside", "Stratford", "Cornwall", "Montague"],
    blogCategories: ["PEI"],
    faqs: [
      { q: "What's the PEI allowed rent increase?", a: "Set annually by the Island Regulatory and Appeals Commission. The 2025 allowed increase is set in late 2024. Central Rentals tracks it live." },
    ],
  },
  {
    slug: "yukon", code: "YT", name: "Yukon",
    pop: "45K",
    tagline: "Built around Yukon Residential Landlord and Tenant Act and small-portfolio operators in Whitehorse.",
    pains: ["No territory-wide rent cap, but Yukon's notice periods are longer than most provinces.", "Mostly small-portfolio landlords with no software — spreadsheet errors compound fast in cold-climate maintenance schedules.", "Limited dispute-resolution capacity in Whitehorse — most cases take 3+ months."],
    features: [
      { icon: FileText, t: "Yukon RLTA lease", d: "Territorial rental agreement with cold-weather habitability clauses (heat, snow removal, plowing)." },
      { icon: Receipt, t: "Notice + service log", d: "Long-form notice periods with auto-served-date math." },
      { icon: Building2, t: "Cold-weather maintenance", d: "Winter prep, snow load, and heating-system inspection templates for Yukon properties." },
    ],
    cities: ["Whitehorse", "Dawson City", "Watson Lake", "Haines Junction"],
    blogCategories: ["Yukon"],
    faqs: [
      { q: "Does Yukon have rent control?", a: "No. But notice periods are longer — 2 months for monthly tenancies — so plan ahead." },
    ],
  },
  {
    slug: "northwest-territories", code: "NT", name: "Northwest Territories",
    pop: "45K",
    tagline: "Built around the Rental Office, remote-territory workflows, and NWT's unique mobile-home rules.",
    pains: ["Remote tenancies — many landlords manage from southern Canada and never visit the unit.", "Mobile-home parks fall under the Mobile Home Sites Regulations — separate rules from regular tenancies.", "Yellowknife-only Rental Office means filings often require mail or e-filing with strict format rules."],
    features: [
      { icon: FileText, t: "NWT lease generator", d: "Territorial rental agreement with optional Mobile Home Sites addendum." },
      { icon: Scale, t: "Rental Office e-filing helper", d: "Application package formatted for the Yellowknife Rental Office." },
      { icon: Building2, t: "Remote-landlord workflow", d: "Property checklist + remote-inspection scheduling for landlords managing from Edmonton or Calgary." },
    ],
    cities: ["Yellowknife", "Hay River", "Inuvik", "Fort Smith"],
    blogCategories: ["Northwest Territories"],
    faqs: [
      { q: "Are mobile homes treated differently in NWT?", a: "Yes — the Mobile Home Sites Regulations under the Residential Tenancies Act govern pad-rental sites with different notice rules and fee structures." },
    ],
  },
  {
    slug: "nunavut", code: "NU", name: "Nunavut",
    pop: "40K",
    tagline: "Built around Nunavut's Rental Office, Inuktitut-language requirements, and northern housing realities.",
    pains: ["Massive housing shortage means most rentals are public or employer-housed — private-landlord docs are scarce.", "Inuktitut-language notices required for many tenant interactions.", "Iqaluit-only Rental Office with limited capacity — disputes can take 4+ months."],
    features: [
      { icon: FileText, t: "Bilingual lease (EN + IU)", d: "Optional Inuktitut translation alongside the standard English rental agreement." },
      { icon: Scale, t: "Rental Office helper", d: "Application + supporting documents formatted for Iqaluit Rental Office submission." },
      { icon: Building2, t: "Northern maintenance", d: "Annual frozen-pipe + heating-system inspection templates for Arctic properties." },
    ],
    cities: ["Iqaluit", "Rankin Inlet", "Arviat", "Baker Lake"],
    blogCategories: ["Nunavut"],
    faqs: [
      { q: "Are Inuktitut leases required in Nunavut?", a: "Not strictly, but the Official Languages Act of Nunavut gives tenants the right to receive landlord communications in Inuktitut. Central Rentals provides side-by-side EN+IU lease generation." },
    ],
  },
];

export const PROVINCE_SLUGS = PROVINCES.map((p) => p.slug);
export const PROVINCE_INDEX = PROVINCES.reduce((m, p) => { m[p.slug] = p; return m; }, {});

export default function Province() {
  const { province } = useParams();
  const prov = PROVINCE_INDEX[province];
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${window.location.origin}/api/blog/posts`);
        const all = r.ok ? await r.json() : [];
        const filtered = (Array.isArray(all) ? all : []).filter((p) =>
          prov && (prov.blogCategories || []).includes(p.category)
        ).slice(0, 6);
        // De-duplicate consecutive hero images so the strip never shows
        // the same photo twice in a row. Deterministic rotation over a
        // small fallback pool keyed by post slug.
        const _POOL = [
          "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=70",
          "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=70",
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=70",
          "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=70",
          "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=70",
          "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=70",
        ];
        const _hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
        let _prev = null;
        const diversified = filtered.map((p) => {
          let img = p.hero_image || _POOL[_hash(p.slug || "") % _POOL.length];
          if (img === _prev) {
            const idx = Math.max(0, _POOL.indexOf(img));
            img = _POOL[(idx + 1) % _POOL.length];
          }
          _prev = img;
          return { ...p, hero_image: img };
        });
        if (!cancelled) setPosts(diversified);
      } catch { if (!cancelled) setPosts([]); }
    })();
    return () => { cancelled = true; };
  }, [prov]);

  // Per-page SEO meta + JSON-LD (LocalBusiness service-area + FAQ).
  useEffect(() => {
    if (!prov) return;
    const url = `https://centralrentals.ca/landlord-software/${prov.slug}`;
    const title = `Property management software for ${prov.name} landlords · Central Rentals`;
    const desc = `Built for ${prov.name} landlords. ${prov.tagline} Paid plans from CA$35/month · Cancel anytime required.`;
    document.title = title;
    const setTag = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      setTag('meta[name="description"]', { name: "description", content: desc }),
      setTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setTag('meta[property="og:type"]', { property: "og:type", content: "website" }),
      setTag('meta[property="og:title"]', { property: "og:title", content: title }),
      setTag('meta[property="og:description"]', { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]', { property: "og:url", content: url }),
      setTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      setTag('meta[name="twitter:title"]', { name: "twitter:title", content: title }),
      setTag('meta[name="twitter:description"]', { name: "twitter:description", content: desc }),
    ];

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": `Central Rentals — Property Management Software for ${prov.name}`,
          "serviceType": "Property management software",
          "provider": { "@type": "Organization", "name": "Central Rentals Canada", "url": "https://centralrentals.ca/" },
          "areaServed": { "@type": "AdministrativeArea", "name": prov.name, "addressCountry": "CA" },
          "url": url,
          "description": desc,
        },
        // LocalBusiness — helps Google surface the page in local SERP packs
        // for queries like "property management software [province]".
        {
          "@type": "LocalBusiness",
          "@id": `${url}#business`,
          "name": `Central Rentals — ${prov.name}`,
          "description": `Property management + rental software for landlords in ${prov.name}. Lease generation, rent collection via Stripe, smart maintenance triage, CRA tax exports.`,
          "url": url,
          "telephone": "+1-587-803-0014",
          "image": "https://centralrentals.ca/og-image.png",
          "priceRange": "$",
          "address": { "@type": "PostalAddress", "addressRegion": prov.name, "addressCountry": "CA" },
          "areaServed": { "@type": "AdministrativeArea", "name": prov.name, "addressCountry": "CA" },
          "knowsAbout": [
            `${prov.name} Residential Tenancies Act`,
            `${prov.name} landlord forms`,
            `${prov.name} rent control`,
            "Smart maintenance triage",
            "peer-to-peer rent tracking",
            "CRA T776 export",
          ],
          "sameAs": [
            "https://www.facebook.com/centralrentalscanada",
            "https://www.linkedin.com/company/central-rentals-canada",
          ],
        },
        // SoftwareApplication — eligible for comparison-style rich snippets
        // and "Best property management software" SERP carousels.
        {
          "@type": "SoftwareApplication",
          "name": `Central Rentals Canada — ${prov.name} property management software`,
          "alternateName": [
            `${prov.name} property management software`,
            `Central Rentals ${prov.name}`,
            `Rental software ${prov.name}`,
          ],
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Property Management Software",
          "operatingSystem": "Web, iOS, Android",
          "url": url,
          "inLanguage": ["en-CA", "fr-CA"],
          "audience": {
            "@type": "Audience",
            "audienceType": `${prov.name} landlords, property managers, and rental tenants`,
          },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": prov.name,
            "address": { "@type": "PostalAddress", "addressCountry": "CA", "addressRegion": prov.code },
          },
          "offers": {
            "@type": "Offer",
            "name": "Starter",
            "price": "60.00",
            "priceCurrency": "CAD",
            "availability": "https://schema.org/InStock",
            "url": "https://centralrentals.ca/pricing",
          },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "127" },
          "publisher": { "@id": "https://centralrentals.ca/#organization" },
        },
        // Service schema — anchors this page to province-specific
        // rental-management searches ("property management Ontario",
        // "landlord software BC", "gestion locative Québec"…).
        {
          "@type": "Service",
          "@id": `${url}#service`,
          "name": `${prov.name} property management software`,
          "serviceType": "Property Management Software",
          "provider": { "@id": "https://centralrentals.ca/#organization" },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": prov.name,
            "address": { "@type": "PostalAddress", "addressCountry": "CA", "addressRegion": prov.code },
          },
          "url": url,
          "description": prov.tagline,
          "offers": {
            "@type": "Offer",
            "priceCurrency": "CAD",
            "price": "60.00",
            "availability": "https://schema.org/InStock",
          },
        },
        {
          "@type": "FAQPage",
          "mainEntity": prov.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
      ],
    };
    let script = document.head.querySelector('script[data-province-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-province-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    // Quebec-only hreflang alternates pointing to the French-Canadian
    // version of this page so Google serves the right locale.
    const hreflangs = [];
    if (prov.slug === "quebec") {
      const FR_URL = "https://centralrentals.ca/fr/landlord-software/quebec";
      const addAlt = (hreflang, href) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", hreflang);
        link.setAttribute("href", href);
        link.setAttribute("data-hreflang-qc-en", "1");
        document.head.appendChild(link);
        hreflangs.push(link);
      };
      addAlt("en-CA", url);
      addAlt("fr-CA", FR_URL);
      addAlt("x-default", url);
    }

    return () => {
      tags.forEach((el) => el && el.remove());
      if (script) script.remove();
      hreflangs.forEach((el) => el && el.remove());
    };
  }, [prov]);

  if (!prov) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`province-${prov.slug}`}>
      <PublicHeader light={true}/>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">
          {prov.code} · Property management software
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.02] text-slate-900">
          Built for <span className="text-emerald-700">{prov.name}</span> landlords.
        </h1>
        <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed">{prov.tagline}</p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link to="/signup?role=landlord&plan=professional" data-testid={`province-${prov.slug}-cta`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 hover:border-emerald-700 text-slate-800 font-semibold">
            Read the {prov.name} field notes
          </Link>
        </div>

        <SEOTrustBlock
          title={`Property management software for ${prov.name} landlords`}
          canonicalUrl={`https://centralrentals.ca/landlord-software/${prov.slug}`}
          province={{ code: prov.code, name: prov.name, slug: prov.slug }}
          kind="landing"
          readingMinutes={6}
        />

        {/* Social share — drives in-app discovery on FB/LinkedIn.
            Real shares from real users are the #1 ranking signal both
            algorithms use for the "Discover" feeds. */}
        <div className="mt-7 flex items-center gap-2 flex-wrap" data-testid={`province-${prov.slug}-share`}>
          <span className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mr-1">Share this page</span>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://centralrentals.ca/landlord-software/${prov.slug}`)}`}
            target="_blank" rel="noreferrer"
            data-testid={`share-fb-${prov.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors"
          ><Facebook className="w-3.5 h-3.5"/>Share on Facebook</a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://centralrentals.ca/landlord-software/${prov.slug}`)}`}
            target="_blank" rel="noreferrer"
            data-testid={`share-li-${prov.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors"
          ><Linkedin className="w-3.5 h-3.5"/>Share on LinkedIn</a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Property management software built for ${prov.name} landlords — `)}&url=${encodeURIComponent(`https://centralrentals.ca/landlord-software/${prov.slug}`)}`}
            target="_blank" rel="noreferrer"
            data-testid={`share-x-${prov.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200 transition-colors"
          ><Twitter className="w-3.5 h-3.5"/>Share on X</a>
        </div>
      </section>

      {/* Pain points */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">What {prov.name} landlords keep losing time on</div>
        <div className="grid md:grid-cols-3 gap-4">
          {prov.pains.map((p, i) => (
            <div key={i} className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="text-2xl font-display font-bold text-slate-300">{(i+1).toString().padStart(2, "0")}</div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
            What we ship specifically for {prov.name}.
          </h2>
          <div className="mt-9 grid md:grid-cols-3 gap-5">
            {prov.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5"/>
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{f.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">Used by landlords across {prov.name}</div>
        <div className="flex flex-wrap gap-2">
          {prov.cities.map((c) => (
            <div key={c} className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 font-medium">{c}</div>
          ))}
        </div>
      </section>

      {/* Blog feed (internal linking) */}
      {posts.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Field notes</div>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-1">
                  Latest for {prov.name} landlords
                </h2>
              </div>
              <Link to="/blog" className="text-sm font-semibold text-emerald-700 hover:underline whitespace-nowrap">All posts →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl border border-slate-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all overflow-hidden">
                  {p.hero_image && (
                    <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                      <img src={p.hero_image} alt={p.hero_alt || p.title} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{p.category}</div>
                    <h3 className="font-display mt-2 text-base font-bold leading-snug text-slate-900 group-hover:text-emerald-800">{p.title}</h3>
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>{p.published_at ? new Date(p.published_at).toLocaleDateString("en-CA", {month: "short", day: "numeric", year: "numeric"}) : ""}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{p.read_mins} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-14">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Frequently asked</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-2 mb-6">
          {prov.name} landlord questions
        </h2>
        <div className="space-y-3">
          {prov.faqs.map((f, i) => (
            <details key={i} className="group p-5 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors">
              <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-1 flex-none"/>
                <span className="flex-1">{f.q}</span>
              </summary>
              <p className="mt-3 ml-7 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900">
            Ready to spend less time on {prov.name} paperwork?
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">Cancel any time. Cancel any time. Cancel any time.</p>
          <Link to="/signup?role=landlord&plan=professional"
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
          <div className="mt-10 max-w-md mx-auto text-left">
            <NewsletterSignup source={`province-${prov.slug}`} compact />
          </div>
        </div>
      </section>

      <PublicContactStrip testid={`province-contact-${prov.slug}`} />

    </div>
  );
}
